import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import * as api from "../../../api/cabinet";
import { useCabinet } from "../../../context/CabinetContext";
import BlobCanvas from "../BlobCanvas";
import CabIcon from "../CabIcon";
import { Button, Card } from "../../ui";
import { avatarInitialColor } from "../cabinetUtils";
import "../../../styles/cabinet-profile.css";

/* стоимость смены аватара в алмазах */
const AVATAR_PRICE = 10;

/* классы 1..11 */
const GRADES = Array.from({ length: 11 }, (_, i) => String(i + 1));

/**
 * ProfileScreen — страница «Профиль» кабинета Рублокса.
 * Аналог /settings из Майнкрафтии: имя, фамилия, статус, город/страна, класс,
 * фото профиля (смена за 10 💎). Сохранение через PUT /users/profile (multipart).
 */
export default function ProfileScreen() {
  const navigate = useNavigate();
  const { profile, refreshProfile, refreshEconomy, diamonds, displayName } =
    useCabinet();

  // поля формы
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [city, setCity] = useState("");
  const [country, setCountry] = useState("");
  const [status, setStatus] = useState("");
  const [grade, setGrade] = useState("");

  // фото — текущий URL (для превью) и выбранный новый файл
  const [photoUrl, setPhotoUrl] = useState(null);
  const [imgFile, setImgFile] = useState(null);
  const [imgPreview, setImgPreview] = useState(null);
  const fileInputRef = useRef(null);

  // ui
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null); // {type:"ok"|"err", text}

  /* заполнить форму из профиля */
  useEffect(() => {
    if (!profile) return;
    setFirstName(profile.firstName || "");
    setLastName(profile.lastName || "");
    setCity(profile.city || "");
    setCountry(profile.country || "");
    setStatus(profile.status || "");
    setGrade(profile.grade ? String(profile.grade) : "");
    setPhotoUrl(api.profilePhotoSrc(profile.photo));
  }, [profile]);

  /* подгружаем профиль на маунте, если в контексте пусто */
  useEffect(() => {
    if (!profile) refreshProfile();
  }, [profile, refreshProfile]);

  /* при выборе файла — локальное превью */
  useEffect(() => {
    if (!imgFile) {
      setImgPreview(null);
      return;
    }
    const url = URL.createObjectURL(imgFile);
    setImgPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [imgFile]);

  const canAfford = (diamonds || 0) >= AVATAR_PRICE;
  const photoChanged = imgFile != null;
  const photoBlocked = photoChanged && !canAfford;

  // Mute от модератора Рублокса (2026-05-26). Бэк отдаёт ISO-строки.
  // Когда модератор сбросил оскорбительный ник/аву — на 7 дней
  // юзер не может ничего менять. Инпуты disabled + подсказка.
  const nickMuteUntil = profile?.nick_mute_until || null;
  const avatarMuteUntil = profile?.avatar_mute_until || null;
  const statusMuteUntil = profile?.status_mute_until || null;
  const isNickMuted = nickMuteUntil && new Date(nickMuteUntil) > new Date();
  const isAvatarMuted = avatarMuteUntil && new Date(avatarMuteUntil) > new Date();
  const isStatusMuted = statusMuteUntil && new Date(statusMuteUntil) > new Date();
  const fmtUntil = (iso) => {
    if (!iso) return "";
    try {
      return new Date(iso).toLocaleString("ru-RU",
        { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
    } catch { return iso; }
  };

  const handleFile = (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setMsg({ type: "err", text: "Выбери картинку (jpg/png/webp)." });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setMsg({ type: "err", text: "Файл больше 5 МБ — выбери поменьше." });
      return;
    }
    setMsg(null);
    setImgFile(file);
  };

  const clearFile = () => {
    setImgFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleSave = async () => {
    if (photoBlocked) {
      setMsg({
        type: "err",
        text: `Для смены фото нужно ${AVATAR_PRICE} 💎. Реши задачи на сайте школы.`,
      });
      return;
    }
    setSaving(true);
    setMsg(null);
    try {
      await api.putMyProfile({
        firstName,
        lastName,
        city,
        country,
        status,
        grade,
        photo: imgFile || undefined,
      });
      // подтянуть свежие данные
      await Promise.all([refreshProfile(), refreshEconomy()]);
      clearFile();
      setMsg({ type: "ok", text: "Изменения сохранены." });
    } catch (e) {
      setMsg({ type: "err", text: e.message || "Не удалось сохранить." });
    } finally {
      setSaving(false);
    }
  };

  const initial = (firstName || displayName || "?").charAt(0).toUpperCase();
  const currentAvatar = imgPreview || photoUrl;

  return (
    <div className="rb-cab-screen rb-prof">
      {/* ── hero ───────────────────────────────────────────────── */}
      <div className="rb-prof__hero">
        <BlobCanvas className="rb-prof__hero-canvas" />
        <div className="rb-prof__hero-inner">
          <div className="rb-prof__hero-text">
            <h1 className="rb-prof__hero-title">
              <CabIcon name="user" size={24} /> Профиль
            </h1>
            <p className="rb-prof__hero-sub">
              Имя, фото и личные данные. Аккаунт общий с Майнкрафтией.
            </p>
          </div>
          <div className="rb-prof__hero-diamonds">
            <CabIcon name="diamond" size={16} />
            {diamonds == null ? "..." : diamonds}
          </div>
        </div>
      </div>

      {/* ── карточка профиля ───────────────────────────────────── */}
      <div className="rb-prof__card">
        <div className="rb-prof__card-title">
          <CabIcon name="userCircle" size={20} /> О себе
        </div>

        <div className="rb-prof__grid">
          {/* левая колонка — аватар */}
          <div className="rb-prof__avatar-col">
            <div className="rb-prof__avatar-wrap">
              {currentAvatar ? (
                <img
                  className="rb-prof__avatar"
                  src={currentAvatar}
                  alt="Аватар"
                />
              ) : (
                <span
                  className="rb-prof__avatar"
                  style={{ background: avatarInitialColor(firstName || "?") }}
                >
                  {initial}
                </span>
              )}
            </div>
            <div className="rb-prof__avatar-label">Фото профиля</div>
            <div
              className={`rb-prof__avatar-hint ${
                photoChanged && canAfford ? "rb-prof__avatar-hint--paid" : ""
              } ${photoBlocked ? "rb-prof__avatar-hint--err" : ""}`}
            >
              <CabIcon name="diamond" size={12} />
              {photoChanged
                ? canAfford
                  ? `Спишется ${AVATAR_PRICE} при сохранении`
                  : `Нужно ${AVATAR_PRICE} — не хватает алмазов`
                : `Смена — ${AVATAR_PRICE} алмазов`}
            </div>

            <label className="rb-prof__file-btn"
                   style={isAvatarMuted ? { opacity: 0.5, cursor: "not-allowed" } : undefined}
                   title={isAvatarMuted ? `Смена аватара заблокирована модератором до ${fmtUntil(avatarMuteUntil)}` : ""}>
              <CabIcon name={isAvatarMuted ? "lock" : (imgFile ? "check" : "user")} size={15} />
              <span>{isAvatarMuted ? "Заблокировано модератором" : (imgFile ? "Файл выбран" : "Загрузить фото")}</span>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                disabled={isAvatarMuted}
                onChange={(e) => handleFile(e.target.files?.[0] || null)}
                hidden
              />
            </label>
            {isAvatarMuted && (
              <div style={{ color: "#ff9b3a", fontSize: 12, marginTop: 6 }}>
                Модератор сбросил аватар. Менять можно с {fmtUntil(avatarMuteUntil)}.
              </div>
            )}
            {imgFile && !isAvatarMuted && (
              <button
                type="button"
                className="rb-prof__file-clear"
                onClick={clearFile}
              >
                Отменить
              </button>
            )}
          </div>

          {/* правая колонка — поля */}
          <div className="rb-prof__fields">
            <label className="rb-prof__field">
              <span>Имя {isNickMuted && <span style={{ color: "#ff9b3a" }} title="Смена ника заблокирована модератором">🔒</span>}</span>
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="Как тебя зовут"
                maxLength={30}
                disabled={isNickMuted}
                style={isNickMuted ? { opacity: 0.6, cursor: "not-allowed" } : undefined}
                title={isNickMuted ? `Смена ника заблокирована модератором до ${fmtUntil(nickMuteUntil)}` : ""}
              />
              {isNickMuted && (
                <small style={{ color: "#ff9b3a", fontSize: 11 }}>
                  Модератор сбросил ник. Менять можно с {fmtUntil(nickMuteUntil)}.
                </small>
              )}
            </label>
            <label className="rb-prof__field">
              <span>Фамилия</span>
              <input
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Необязательно"
                maxLength={40}
              />
            </label>
            <label className="rb-prof__field">
              <span>Город</span>
              <input
                type="text"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                placeholder="Краснодар"
                maxLength={40}
              />
            </label>
            <label className="rb-prof__field">
              <span>Страна</span>
              <input
                type="text"
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                placeholder="Россия"
                maxLength={40}
              />
            </label>
            <label className="rb-prof__field rb-prof__field--full">
              <span>Статус {isStatusMuted && <span style={{ color: "#ff9b3a" }}>🔒</span>}</span>
              <input
                type="text"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                placeholder="Учу Python и собираю свои игры в Рублоксе"
                maxLength={80}
                disabled={isStatusMuted}
                style={isStatusMuted ? { opacity: 0.6 } : undefined}
                title={isStatusMuted ? `Смена статуса заблокирована модератором до ${fmtUntil(statusMuteUntil)}` : ""}
              />
              {isStatusMuted && (
                <small style={{ color: "#ff9b3a", fontSize: 11 }}>
                  Модератор сбросил статус. Менять можно с {fmtUntil(statusMuteUntil)}.
                </small>
              )}
            </label>
            <label className="rb-prof__field rb-prof__field--full">
              <span>
                Класс <small>— укажи, в каком классе ты учишься</small>
              </span>
              <select
                value={grade}
                onChange={(e) => setGrade(e.target.value)}
              >
                <option value="">Не указан</option>
                {GRADES.map((g) => (
                  <option key={g} value={g}>
                    {g} класс
                  </option>
                ))}
              </select>
            </label>
          </div>
        </div>

        {/* сообщение */}
        {msg && (
          <div
            className={`rb-prof__msg ${
              msg.type === "ok" ? "rb-prof__msg--ok" : "rb-prof__msg--err"
            }`}
          >
            <CabIcon
              name={msg.type === "ok" ? "check" : "warning"}
              size={14}
            />
            {msg.text}
          </div>
        )}

        {/* кнопка сохранения */}
        <button
          className="rb-prof__save"
          onClick={handleSave}
          disabled={saving || photoBlocked}
        >
          {saving ? "Сохраняем…" : "Сохранить изменения"}
        </button>
      </div>

      {/* Темы оформления жили ЗДЕСЬ вторым магазином — с собственной
          вёрсткой карточек, своими кнопками покупки и своим списанием
          алмазов. Ровно та болезнь, ради лечения которой делался единый
          магазин: одна и та же покупка в двух местах разъезжается.
          Теперь темы только в /app/shop, а отсюда — ссылка туда. */}
      <Card className="rb-prof__shoplink">
        <CabIcon name="palette" size={22} />
        <div className="rb-prof__shoplink-text">
          <div className="rb-prof__shoplink-title">Оформление кабинета</div>
          <div className="rb-prof__shoplink-sub">
            Темы, рамки аватара и цвет ника — в магазине.
          </div>
        </div>
        <Button variant="ghost" onClick={() => navigate("/app/shop")}>
          В магазин
        </Button>
      </Card>
    </div>
  );
}
